/**
 * The IATA delay codes (AHM 730), the list a delay reason entered by hand is
 * checked against.
 *
 * The platform keeps the same list in backend/api/slt/movements/delay_codes.py,
 * so the demonstration refuses what the platform refuses. Change both
 * together. A code is two digits, optionally followed by one sub-code letter
 * (AHM 731, such as 93A); only the digits are checked. The two-letter forms
 * (RA, PD …) are refused with a hint: the boards count delays by the numeric
 * code, and a second spelling of the same reason would split the count.
 */
export const DELAY_CODES: Record<string, string> = {
  "00": "Airline internal code",
  "01": "Airline internal code",
  "02": "Airline internal code",
  "03": "Airline internal code",
  "04": "Airline internal code",
  "05": "Airline internal code",
  "06": "No gate or stand, own airline activity",
  "09": "Scheduled ground time under the minimum",
  "11": "Late check-in, after the deadline",
  "12": "Late check-in, congestion",
  "13": "Check-in error",
  "14": "Oversales or booking error",
  "15": "Boarding: missing or late travellers",
  "16": "Commercial request, VIP or press",
  "17": "Catering order late or wrong",
  "18": "Bag processing",
  "19": "Reduced mobility, boarding or leaving",
  "21": "Cargo documents",
  "22": "Cargo late to the hold",
  "23": "Cargo accepted late",
  "24": "Cargo packing",
  "25": "Cargo oversales",
  "26": "Cargo prepared late",
  "27": "Mail documents or packing",
  "28": "Mail late to the hold",
  "29": "Mail accepted late",
  "31": "Load documents late or wrong",
  "32": "Loading or unloading",
  "33": "Loading equipment",
  "34": "Servicing equipment",
  "35": "Cleaning",
  "36": "Fueling or defueling",
  "37": "Catering delivered or loaded late",
  "38": "Containers (ULD)",
  "39": "Technical ground equipment",
  "41": "Defects",
  "42": "Scheduled maintenance, late release",
  "43": "Unscheduled maintenance",
  "44": "Spares and maintenance equipment",
  "45": "AOG spares",
  "46": "Change of unit, technical",
  "47": "Standby unit",
  "48": "Cabin layout change",
  "51": "Damage in flight",
  "52": "Damage on the ground",
  "55": "Departure control system",
  "56": "Cargo system",
  "57": "Flight plan system",
  "58": "Other system",
  "61": "Flight plan late or changed",
  "62": "Fuel or load change",
  "63": "Crew late or procedures",
  "64": "Flight crew shortage",
  "65": "Flight crew request",
  "66": "Cabin crew late",
  "67": "Cabin crew shortage",
  "68": "Cabin crew error or request",
  "69": "Captain's security check",
  "71": "Weather at departure",
  "72": "Weather at destination",
  "73": "Weather en route or alternate",
  "75": "De-icing",
  "76": "Snow, ice, water or sand removal",
  "77": "Ground handling slowed by weather",
  "81": "Flow control, en-route demand",
  "82": "Flow control, ATC staff or equipment",
  "83": "Flow control, destination restriction",
  "84": "Flow control, weather at destination",
  "85": "Mandatory security",
  "86": "Immigration, customs or health",
  "87": "Airport facilities",
  "88": "Restrictions at the destination airport",
  "89": "Restrictions at the departure airport",
  "91": "Waiting for connecting load",
  "92": "Through check-in error",
  "93": "Late inbound unit (rotation)",
  "94": "Cabin crew rotation",
  "95": "Crew rotation",
  "96": "Operations control",
  "97": "Industrial action, own airline",
  "98": "Industrial action, others",
  "99": "Other",
};

const SHAPE = /^(\d{2})([A-Z]?)$/;

/** Null when the code is an IATA delay code, else the reason in words (the platform's words). */
export function checkDelayCode(code: string): string | null {
  const c = String(code ?? "")
    .trim()
    .toUpperCase();
  if (!c) return "a delay code is two digits, like 93";
  const m = SHAPE.exec(c);
  if (!m) {
    if (/^[A-Z]{2}$/.test(c)) return `${c} is the letter form; use the two-digit IATA code, like 93`;
    return `${c} is not an IATA delay code; a code is two digits, like 93`;
  }
  if (!(m[1] in DELAY_CODES)) return `${c} is not an IATA delay code`;
  return null;
}

/** What a code means, in a few words, or undefined. */
export function delayCodeLabel(code: string): string | undefined {
  const m = SHAPE.exec(
    String(code ?? "")
      .trim()
      .toUpperCase(),
  );
  return m ? DELAY_CODES[m[1]] : undefined;
}
